import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { all_routes } from '../../router/all_routes';
import type { AppDispatch, RootState } from '../../../core/data/redux/store';
import { getEmployeeProfile, getMyProfile } from '../../../core/data/redux/employeesSlice';
import {
  createUpdateERequisition,
  deleteDraftERequisition,
  fetchERequisitionMaster,
} from '../../../core/data/redux/eRequisitionSlice';
import {
  FORM_TYPE_LABELS,
  IT_EQUIPMENT_FALLBACK,
  PRINTER_NOTE,
  SUPERVISOR_FALLBACK,
  VPN_COMPLIANCE,
  YES_NO_FALLBACK,
  apiMessage,
  formatDisplayDate,
  isApiSuccess,
  lineItemsOf,
  masterOptions,
  normalizeFormType,
  readSessionUser,
  resolveEmployee,
  toInputDate,
  toSubmitDate,
  unwrapMaster,
  MasterOption,
} from './eRequisition.shared';

const emptyForm = {
  mobileNo: '',
  officialEmailId: '',
  officeLocation: '',
  selectRequestFromType: '',
  alreadyIssueITAsset: '',
  supervisorUnderHOD: '',
  workingFromDate: '',
  vppStatus: '',
  userProfileForVPP: '',
  userProfileForMPR: '',
  accessDurationFrom: '',
  accessDurationTo: '',
  macAddress: '',
  firewallStatus: '',
  services: '',
  systemIPAddress: '',
  antivirus: '',
  operatingSystemVersion: '',
  detailsOfServer: '',
  remarks: '',
};

const ERequisitionITCreate: React.FC = () => {
  const routes = all_routes;
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch<AppDispatch>();

  const employeesState = useSelector((state: RootState) => state.employeesState);
  const eState = useSelector((state: RootState) => state.eRequisitionState);
  const sessionUser = readSessionUser();
  const employee = resolveEmployee(employeesState, sessionUser);

  // App Continue passes the list row and does not call find-one.
  const draftData = (location.state as any)?.draftRequest;

  const master = unwrapMaster(eState?.masterData);
  const formTypesList = masterOptions(
    master,
    'Form_Type',
    Object.entries(FORM_TYPE_LABELS).map(([code, label]) => ({ code, label }))
  );
  const itemOptionsList = masterOptions(master, 'Item_List_Type', IT_EQUIPMENT_FALLBACK);
  const vppStatusOptions = masterOptions(master, 'VPP_Status_Type');
  const userVppOptions = masterOptions(master, 'User_VPP_Type');
  const userMprOptions = masterOptions(master, 'User_MPR_Type');
  const servicesOptions = masterOptions(master, 'Services_Type');
  const firewallStatusOptions = masterOptions(master, 'Firewall_Status_Type');
  const supervisorOptions = masterOptions(master, 'Supervisor_Type', SUPERVISOR_FALLBACK);
  const itAssetOptions = masterOptions(master, 'IT_Asset_Type', YES_NO_FALLBACK);

  const [form, setForm] = useState(emptyForm);
  const [items, setItems] = useState<Array<{ SerialNumber: string; Items: string; ItemLabel: string; Remarks: string; AllotementOrNot: string }>>([]);
  const [showItemModal, setShowItemModal] = useState(false);
  const [newItem, setNewItem] = useState({ Items: '', ItemLabel: '', Remarks: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [compliance, setCompliance] = useState<boolean[]>(VPN_COMPLIANCE.map(() => false));
  const [hydratedKey, setHydratedKey] = useState('');

  const officeLookupStarted = useRef(false);

  useEffect(() => {
    dispatch(getMyProfile({}));
    dispatch(fetchERequisitionMaster({}));
  }, [dispatch]);

  useEffect(() => {
    if (employee.officeLocation || officeLookupStarted.current || !employee.empId) return;
    if (employeesState?.loading !== 'fulfilled' || employeesState?.action !== 'my-profile') return;
    officeLookupStarted.current = true;
    dispatch(getEmployeeProfile({ empId: employee.empId }));
  }, [employee.officeLocation, employee.empId, employeesState?.loading, employeesState?.action, dispatch]);

  useEffect(() => {
    if (!draftData) return;
    const rawItems = lineItemsOf(draftData);
    const key = [
      draftData.id || '',
      draftData.ReferenceNumber || id || '',
      draftData.SelectRequestFromType || '',
      draftData.WorkingFromDate || '',
      draftData.ContactNumber || '',
      draftData.AlreadyIssueITAsset || '',
      draftData.SupervisorUnderHOD || '',
      rawItems.length,
      rawItems.map((line: any) => `${line.SerialNumber || ''}:${line.Items || ''}:${line.Remarks || ''}`).join(','),
    ].join('|');
    if (!key || hydratedKey === key) return;
    setForm({
      ...emptyForm,
      mobileNo: String(draftData.ContactNumber || '').replace(/\D/g, '').slice(0, 10) || employee.mobileNo,
      officialEmailId: draftData.EmailID || employee.email,
      officeLocation: draftData.OfficeLocation || employee.officeLocation,
      selectRequestFromType: normalizeFormType(draftData.SelectRequestFromType),
      alreadyIssueITAsset: draftData.AlreadyIssueITAsset && draftData.AlreadyIssueITAsset !== '0' ? String(draftData.AlreadyIssueITAsset) : '',
      supervisorUnderHOD: draftData.SupervisorUnderHOD && draftData.SupervisorUnderHOD !== '0' ? String(draftData.SupervisorUnderHOD) : '',
      workingFromDate: toInputDate(draftData.WorkingFromDate),
      vppStatus: draftData.VPPStatus && draftData.VPPStatus !== '0' ? String(draftData.VPPStatus) : '',
      userProfileForVPP: draftData.UserProfileForVPP && draftData.UserProfileForVPP !== '0' ? String(draftData.UserProfileForVPP) : '',
      userProfileForMPR: draftData.UserProfileForMPR && draftData.UserProfileForMPR !== '0' ? String(draftData.UserProfileForMPR) : '',
      accessDurationFrom: toInputDate(draftData.AccessDurationFrom),
      accessDurationTo: toInputDate(draftData.AccessDurationTo),
      macAddress: draftData.MacAddress || '',
      firewallStatus: draftData.FirewallStatus && draftData.FirewallStatus !== '0' ? String(draftData.FirewallStatus) : '',
      services: draftData.Services && draftData.Services !== '0' ? String(draftData.Services) : '',
      systemIPAddress: draftData.SystemIPAddress || '',
      antivirus: draftData.Antivirus || '',
      operatingSystemVersion: draftData.OperatingSystemVersion || '',
      detailsOfServer: draftData.DetailsOfServer || '',
      remarks: draftData.ORemarks || '',
    });
    if (Array.isArray(rawItems)) {
      setItems(rawItems.map((it: any, idx: number) => ({
        SerialNumber: it.SerialNumber || String(idx + 1).padStart(2, '0'),
        Items: it.Items || '',
        ItemLabel: it.ItemLabel || it.ItemsText || '',
        Remarks: it.Remarks || '',
        AllotementOrNot: it.AllotementOrNot || '1',
      })));
    }
    setHydratedKey(key);
  }, [draftData, hydratedKey, id]);

  useEffect(() => {
    if (!employee.empId && !employee.mobileNo && !employee.email) return;
    setForm(prev => ({
      ...prev,
      mobileNo: prev.mobileNo || employee.mobileNo,
      officialEmailId: prev.officialEmailId || employee.email,
      officeLocation: prev.officeLocation || employee.officeLocation,
    }));
  }, [employee.empId, employee.mobileNo, employee.email, employee.officeLocation]);

  const selectedType = normalizeFormType(form.selectRequestFromType);
  const selectedTypeObj = formTypesList.find(t => normalizeFormType(t.code) === selectedType);
  const joiningLabel = formatDisplayDate(employee.dateOfJoining) || '-';
  const dobLabel = formatDisplayDate(employee.dateOfBirth) || '-';

  const openItemModal = () => {
    setNewItem({ Items: '', ItemLabel: '', Remarks: '' });
    setShowItemModal(true);
  };

  const saveItem = () => {
    if (!newItem.Items || newItem.Items === '0') {
      toast.error('Please fill Sr. No and select an item');
      return;
    }
    const opt = itemOptionsList.find(o => o.code === newItem.Items);
    setItems(prev => [
      ...prev,
      {
        SerialNumber: String(prev.length + 1).padStart(2, '0'),
        Items: newItem.Items,
        ItemLabel: opt?.label || newItem.ItemLabel,
        Remarks: newItem.Remarks.trim(),
        AllotementOrNot: '1',
      },
    ]);
    setErrors(prev => ({ ...prev, items: '' }));
    setShowItemModal(false);
  };

  const removeItem = (index: number) => {
    setItems(prev => prev.filter((_, idx) => idx !== index).map((it, idx) => ({
      ...it,
      SerialNumber: String(idx + 1).padStart(2, '0'),
    })));
  };

  const validate = (): Record<string, string> => {
    const errs: Record<string, string> = {};
    if (!/^\d{10}$/.test(form.mobileNo)) errs.mobileNo = 'Enter a 10-digit mobile number';
    if (!form.officialEmailId.trim() || !/\S+@\S+\.\S+/.test(form.officialEmailId.trim())) errs.officialEmailId = 'Official email is required';
    if (!form.officeLocation.trim()) errs.officeLocation = 'Office location is required';
    if (!selectedType) errs.selectRequestFromType = 'Please select a request form type';

    if (selectedType === '00') {
      if (!form.supervisorUnderHOD) errs.supervisorUnderHOD = 'Please select an option';
      if (!form.alreadyIssueITAsset) errs.alreadyIssueITAsset = 'Please select an option';
      if (items.length === 0) errs.items = 'Please add at least one line item';
    } else if (selectedType === '11') {
      if (!form.workingFromDate || !toSubmitDate(form.workingFromDate)) errs.workingFromDate = 'From date working in current department is required';
    } else if (selectedType === '12') {
      if (!form.userProfileForVPP) errs.userProfileForVPP = 'Please select an option';
      if (!form.userProfileForMPR) errs.userProfileForMPR = 'Please select an option';
      if (!form.vppStatus) errs.vppStatus = 'Please select an option';
    } else if (selectedType === '13') {
      if (!form.operatingSystemVersion.trim()) errs.operatingSystemVersion = 'This field is required';
      if (!form.antivirus.trim()) errs.antivirus = 'This field is required';
      if (!form.firewallStatus) errs.firewallStatus = 'Please select an option';
      if (!form.macAddress.trim()) errs.macAddress = 'This field is required';
      if (!form.detailsOfServer.trim()) errs.detailsOfServer = 'This field is required';
      if (!form.accessDurationFrom || !toSubmitDate(form.accessDurationFrom)) errs.accessDurationFrom = 'Please select access from date';
      if (!form.accessDurationTo || !toSubmitDate(form.accessDurationTo)) errs.accessDurationTo = 'Please select access to date';
      if (compliance.some(checked => !checked)) errs.compliance = 'Please accept all compliance agreement checkpoints';
    } else if (selectedType === '14') {
      if (!form.services) errs.services = 'Please select an option';
      if (!form.systemIPAddress.trim()) errs.systemIPAddress = 'System IP Address is required';
    }
    return errs;
  };

  const buildPayload = (isDraft: boolean) => {
    if (isDraft) {
      return {
        is_draft: true,
        Action: 'V',
        ...(draftData?.id != null && draftData.id !== '' ? { id: draftData.id } : {}),
        EmpID: employee.empId,
        ContactNumber: form.mobileNo.trim(),
        OfficeLocation: form.officeLocation.trim(),
        EmailID: form.officialEmailId.trim(),
        SelectRequestFromType: selectedType && selectedType !== '0' ? selectedType : '',
      };
    }

    const base: any = {
      is_draft: false,
      Action: 'V',
      ReferenceNumber: '',
      EmpID: employee.empId,
      ContactNumber: form.mobileNo.trim(),
      OfficeLocation: form.officeLocation.trim(),
      EmailID: form.officialEmailId.trim(),
      SelectRequestFromType: selectedType,
    };
    if (selectedType === '00') {
      return {
        ...base,
        AlreadyIssueITAsset: form.alreadyIssueITAsset,
        SupervisorUnderHOD: form.supervisorUnderHOD,
        List_Item: {
          results: items.map(line => ({
            ReferenceNumber: '',
            SerialNumber: line.SerialNumber,
            Items: line.Items,
            Remarks: line.Remarks,
            AllotementOrNot: line.AllotementOrNot || '1',
          })),
        },
      };
    }
    if (selectedType === '11') return { ...base, WorkingFromDate: toSubmitDate(form.workingFromDate) };
    if (selectedType === '12') {
      return {
        ...base,
        VPPStatus: form.vppStatus,
        UserProfileForVPP: form.userProfileForVPP,
        UserProfileForMPR: form.userProfileForMPR,
      };
    }
    if (selectedType === '13') {
      return {
        ...base,
        OperatingSystemVersion: form.operatingSystemVersion,
        Antivirus: form.antivirus,
        AccessDurationFrom: toSubmitDate(form.accessDurationFrom),
        AccessDurationTo: toSubmitDate(form.accessDurationTo),
        MacAddress: form.macAddress,
        FirewallStatus: form.firewallStatus,
        DetailsOfServer: form.detailsOfServer,
      };
    }
    if (selectedType === '14') {
      return {
        ...base,
        Services: form.services,
        SystemIPAddress: form.systemIPAddress.trim(),
        ORemarks: form.remarks,
      };
    }
    return base;
  };

  const handleFormSubmit = async (isDraft: boolean) => {
    if (!isDraft) {
      const errs = validate();
      setErrors(errs);
      const messages = Object.values(errs).filter(Boolean);
      if (messages.length) {
        toast.error(messages[0]);
        return;
      }
    } else if (!selectedType && !form.mobileNo) {
      toast.error('Enter mobile number or select a form type before saving a draft');
      return;
    }
    if (!employee.empId) {
      toast.error('Employee number is missing. Refresh the page and try again.');
      return;
    }

    if (selectedType === '11' && form.workingFromDate && !toSubmitDate(form.workingFromDate)) {
      toast.error('From date must be a valid date (DD-MM-YYYY)');
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await dispatch(createUpdateERequisition(buildPayload(isDraft)));
      if (createUpdateERequisition.fulfilled.match(res) && isApiSuccess(res.payload)) {
        toast.success(isDraft ? 'Draft saved successfully' : 'Request submitted successfully');
        navigate(routes.eRequisitionIT.list);
      } else {
        toast.error(apiMessage(res.payload, 'Submission failed'));
      }
    } catch (err: any) {
      toast.error(err?.message || 'Submission error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteDraft = async () => {
    if (!draftData?.id) {
      toast.error('This draft cannot be deleted until it is saved.');
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await dispatch(deleteDraftERequisition({ id: draftData.id }));
      if (deleteDraftERequisition.fulfilled.match(res) && isApiSuccess(res.payload)) {
        toast.success('Draft deleted');
        navigate(routes.eRequisitionIT.list);
      } else {
        toast.error(apiMessage(res.payload, 'Failed to delete draft'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const setField = (key: string, value: string) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setErrors(prev => ({ ...prev, [key]: '' }));
  };

  const profileFields = useMemo(() => [
    { l: 'Employee Name', v: employee.employeeName || '-' },
    { l: 'Employee No.', v: employee.empId || '-' },
    { l: 'Designation', v: employee.designation || '-' },
    { l: 'Department', v: employee.department || '-' },
    { l: 'Wing (Project/O&M)', v: employee.wing || '-' },
    { l: 'Joining Date', v: joiningLabel },
  ], [employee, joiningLabel]);

  const SelectField = ({
    label, value, error, options, onChange, placeholder,
  }: {
    label: string; value: string; error?: string; options: MasterOption[]; onChange: (v: string) => void; placeholder: string;
  }) => (
    <div className="col-md-6 mt-2">
      <label className="form-label text-dark fw-semibold small">{label} <span className="text-danger">*</span></label>
      <select className={`form-select ${error ? 'is-invalid' : ''}`} value={value} onChange={e => onChange(e.target.value)}>
        <option value="">{placeholder}</option>
        {options.map(o => <option key={o.code} value={o.code}>{o.label}</option>)}
      </select>
      {error && <div className="invalid-feedback">{error}</div>}
    </div>
  );

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div>
            <h3 className="page-title mb-0 text-dark fw-bold">
              {id ? `E-Requisition (IT) - Continue Draft (#${id})` : 'E-Requisition (IT) - New Request'}
            </h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item"><Link to={routes.employeeDashboard || '#'}>Dashboard</Link></li>
                <li className="breadcrumb-item"><Link to={routes.eRequisitionIT.list}>E-Requisition (IT)</Link></li>
                <li className="breadcrumb-item active">{id ? 'Continue' : 'Create'}</li>
              </ol>
            </nav>
          </div>
        </div>

        <div className="card border-0 shadow-sm">
          <div className="card-header bg-white border-bottom py-3">
            <h5 className="mb-0 fw-bold text-dark">Application Form</h5>
          </div>
          <div className="card-body">
            <form onSubmit={e => e.preventDefault()}>
              <div className="row g-3">
                {profileFields.map(f => (
                  <div className="col-md-4 col-sm-6" key={f.l}>
                    <label className="form-label text-dark fw-semibold small">{f.l}</label>
                    <input type="text" className="form-control bg-light" value={f.v} readOnly />
                  </div>
                ))}

                <div className="col-md-4 col-sm-6">
                  <label className="form-label text-dark fw-semibold small">Mobile No. <span className="text-danger">*</span></label>
                  <input
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel"
                    pattern="[0-9]*"
                    className={`form-control ${errors.mobileNo ? 'is-invalid' : ''}`}
                    value={form.mobileNo}
                    maxLength={10}
                    placeholder="10-digit mobile"
                    onKeyDown={e => {
                      if (e.key.length === 1 && !/\d/.test(e.key)) e.preventDefault();
                    }}
                    onPaste={e => {
                      e.preventDefault();
                      const digits = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 10);
                      setField('mobileNo', digits);
                    }}
                    onChange={e => setField('mobileNo', e.target.value.replace(/\D/g, '').slice(0, 10))}
                  />
                  {errors.mobileNo && <div className="invalid-feedback">{errors.mobileNo}</div>}
                </div>

                <div className="col-md-4 col-sm-6">
                  <label className="form-label text-dark fw-semibold small">Official Email <span className="text-danger">*</span></label>
                  <input type="email" className={`form-control ${errors.officialEmailId ? 'is-invalid' : ''}`} value={form.officialEmailId} onChange={e => setField('officialEmailId', e.target.value)} />
                  {errors.officialEmailId && <div className="invalid-feedback">{errors.officialEmailId}</div>}
                </div>

                <div className="col-md-4 col-sm-6">
                  <label className="form-label text-dark fw-semibold small">Office Location <span className="text-danger">*</span></label>
                  <input type="text" className={`form-control ${errors.officeLocation ? 'is-invalid' : ''}`} value={form.officeLocation} onChange={e => setField('officeLocation', e.target.value)} />
                  {errors.officeLocation && <div className="invalid-feedback">{errors.officeLocation}</div>}
                </div>

                <div className="col-md-12">
                  <label className="form-label text-dark fw-semibold small">Select request form type <span className="text-danger">*</span></label>
                  <div className={`form-select bg-white ${errors.selectRequestFromType ? 'is-invalid' : ''}`} role="button" onClick={() => setShowTypeModal(true)}>
                    {selectedTypeObj ? selectedTypeObj.label : 'Select option'}
                  </div>
                  {errors.selectRequestFromType && <div className="invalid-feedback d-block">{errors.selectRequestFromType}</div>}
                </div>

                {selectedType === '00' && (
                  <>
                    <div className="col-12">
                      <div className="p-3 rounded-3 mb-1" style={{ background: '#f8e8c8' }}>
                        <h6 className="fw-bold mb-2 text-danger">IT Equipment Requisition Form</h6>
                        <div className="small mb-0"><span className="text-danger fw-bold">* </span>{PRINTER_NOTE}</div>
                      </div>
                    </div>
                    <div className="col-12 d-flex gap-2">
                      <button type="button" className="btn btn-danger btn-sm" onClick={openItemModal}>Add Line</button>
                      <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => items.length && removeItem(items.length - 1)}>Delete Line</button>
                    </div>
                    {errors.items && <div className="col-12 text-danger small">{errors.items}</div>}
                    {items.map((line, idx) => (
                      <div className="col-md-6" key={`${line.SerialNumber}-${idx}`}>
                        <div className="border rounded-3 p-3 h-100">
                          <div className="d-flex justify-content-between"><span className="text-muted">Sr. No.</span><span className="fw-semibold">{line.SerialNumber}</span></div>
                          <div className="d-flex justify-content-between mt-1"><span className="text-muted">Items</span><span className="fw-semibold">{line.ItemLabel || itemOptionsList.find(o => o.code === line.Items)?.label || line.Items}</span></div>
                          <div className="d-flex justify-content-between mt-1"><span className="text-muted">Remarks</span><span>{line.Remarks || '-'}</span></div>
                          <button type="button" className="btn btn-link text-danger p-0 mt-2" onClick={() => removeItem(idx)}>Delete</button>
                        </div>
                      </div>
                    ))}
                    <SelectField
                      label="Only for requirement of Supervisors: PCs issued to Supervisors are not more than 50% of Supervisors working under concerned HOD"
                      value={form.supervisorUnderHOD}
                      error={errors.supervisorUnderHOD}
                      options={supervisorOptions}
                      placeholder="Select option"
                      onChange={v => setField('supervisorUnderHOD', v)}
                    />
                    <SelectField
                      label="Already Issued IT assets (if any)"
                      value={form.alreadyIssueITAsset}
                      error={errors.alreadyIssueITAsset}
                      options={itAssetOptions}
                      placeholder="Select option"
                      onChange={v => setField('alreadyIssueITAsset', v)}
                    />
                  </>
                )}

                {selectedType === '11' && (
                  <>
                    <div className="col-12"><h6 className="fw-bold border-bottom pb-2">Eoffice form</h6></div>
                    <div className="col-md-4">
                      <label className="form-label small fw-semibold">Date of Birth</label>
                      <input className="form-control bg-light" value={dobLabel} readOnly />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small fw-semibold">Type of Appointment</label>
                      <input className="form-control bg-light" value={employee.appointmentType} readOnly />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small fw-semibold">From date working in current department <span className="text-danger">*</span></label>
                      <input type="date" className={`form-control ${errors.workingFromDate ? 'is-invalid' : ''}`} value={form.workingFromDate} onChange={e => setField('workingFromDate', e.target.value)} />
                      <div className="form-text">Saved as {form.workingFromDate ? (toSubmitDate(form.workingFromDate) || 'invalid date') : 'DD-MM-YYYY'}</div>
                      {errors.workingFromDate && <div className="invalid-feedback">{errors.workingFromDate}</div>}
                    </div>
                  </>
                )}

                {selectedType === '12' && (
                  <>
                    <div className="col-12"><h6 className="fw-bold border-bottom pb-2">VPP/MPR</h6></div>
                    <SelectField label="Select if you want authorization for VPP" value={form.userProfileForVPP} error={errors.userProfileForVPP} options={userVppOptions} placeholder="Select option" onChange={v => setField('userProfileForVPP', v)} />
                    <SelectField label="Select if you want authorization for MPR" value={form.userProfileForMPR} error={errors.userProfileForMPR} options={userMprOptions} placeholder="Select option" onChange={v => setField('userProfileForMPR', v)} />
                    <SelectField label="Status" value={form.vppStatus} error={errors.vppStatus} options={vppStatusOptions} placeholder="Select option" onChange={v => setField('vppStatus', v)} />
                  </>
                )}

                {selectedType === '13' && (
                  <>
                    <div className="col-12"><h6 className="fw-bold border-bottom pb-2">SSL VPN</h6></div>
                    <div className="col-md-4">
                      <label className="form-label small fw-semibold">Operating System Name and Version <span className="text-danger">*</span></label>
                      <input className={`form-control ${errors.operatingSystemVersion ? 'is-invalid' : ''}`} value={form.operatingSystemVersion} onChange={e => setField('operatingSystemVersion', e.target.value)} />
                      {errors.operatingSystemVersion && <div className="invalid-feedback">{errors.operatingSystemVersion}</div>}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small fw-semibold">Antivirus (Name and Version) <span className="text-danger">*</span></label>
                      <input className={`form-control ${errors.antivirus ? 'is-invalid' : ''}`} value={form.antivirus} onChange={e => setField('antivirus', e.target.value)} />
                      {errors.antivirus && <div className="invalid-feedback">{errors.antivirus}</div>}
                    </div>
                    <SelectField label="Firewall status" value={form.firewallStatus} error={errors.firewallStatus} options={firewallStatusOptions} placeholder="Select option" onChange={v => setField('firewallStatus', v)} />
                    <div className="col-md-4">
                      <label className="form-label small fw-semibold">System MAC address <span className="text-danger">*</span></label>
                      <input className={`form-control ${errors.macAddress ? 'is-invalid' : ''}`} value={form.macAddress} onChange={e => setField('macAddress', e.target.value)} />
                      {errors.macAddress && <div className="invalid-feedback">{errors.macAddress}</div>}
                    </div>
                    <div className="col-md-8">
                      <label className="form-label small fw-semibold">Details of server you need to access <span className="text-danger">*</span></label>
                      <input className={`form-control ${errors.detailsOfServer ? 'is-invalid' : ''}`} value={form.detailsOfServer} onChange={e => setField('detailsOfServer', e.target.value)} />
                      {errors.detailsOfServer && <div className="invalid-feedback">{errors.detailsOfServer}</div>}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small fw-semibold">Valid From <span className="text-danger">*</span></label>
                      <input type="date" className={`form-control ${errors.accessDurationFrom ? 'is-invalid' : ''}`} value={form.accessDurationFrom} onChange={e => setField('accessDurationFrom', e.target.value)} />
                      {form.accessDurationFrom && <div className="form-text">{toSubmitDate(form.accessDurationFrom)}</div>}
                      {errors.accessDurationFrom && <div className="invalid-feedback">{errors.accessDurationFrom}</div>}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small fw-semibold">To <span className="text-danger">*</span></label>
                      <input type="date" className={`form-control ${errors.accessDurationTo ? 'is-invalid' : ''}`} value={form.accessDurationTo} onChange={e => setField('accessDurationTo', e.target.value)} />
                      {form.accessDurationTo && <div className="form-text">{toSubmitDate(form.accessDurationTo)}</div>}
                      {errors.accessDurationTo && <div className="invalid-feedback">{errors.accessDurationTo}</div>}
                    </div>
                    <div className="col-12">
                      <h6 className="fw-bold">Compliance agreement</h6>
                      {VPN_COMPLIANCE.map((text, index) => (
                        <div className="form-check" key={text}>
                          <input className="form-check-input" type="checkbox" id={`vpn-${index}`} checked={compliance[index]} onChange={() => {
                            setCompliance(prev => prev.map((c, i) => i === index ? !c : c));
                            setErrors(prev => ({ ...prev, compliance: '' }));
                          }} />
                          <label className="form-check-label small" htmlFor={`vpn-${index}`}>{text}</label>
                        </div>
                      ))}
                      {errors.compliance && <div className="text-danger small mt-1">{errors.compliance}</div>}
                    </div>
                  </>
                )}

                {selectedType === '14' && (
                  <>
                    <div className="col-12"><h6 className="fw-bold border-bottom pb-2">Third-Party Email Access Request</h6></div>
                    <SelectField label="Services" value={form.services} error={errors.services} options={servicesOptions} placeholder="Select option" onChange={v => setField('services', v)} />
                    <div className="col-md-6 mt-2">
                      <label className="form-label small fw-semibold">System IP Address <span className="text-danger">*</span></label>
                      <input className={`form-control ${errors.systemIPAddress ? 'is-invalid' : ''}`} placeholder="e.g. 10.10.20.30" value={form.systemIPAddress} onChange={e => setField('systemIPAddress', e.target.value)} />
                      {errors.systemIPAddress && <div className="invalid-feedback">{errors.systemIPAddress}</div>}
                    </div>
                    <div className="col-12">
                      <label className="form-label small fw-semibold">Any other remarks</label>
                      <textarea className="form-control" rows={2} value={form.remarks} onChange={e => setField('remarks', e.target.value)} />
                    </div>
                  </>
                )}
              </div>

              <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
                <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(-1)} disabled={isSubmitting}>Cancel</button>
                {id && draftData?.id != null && (
                  <button type="button" className="btn btn-outline-danger" onClick={handleDeleteDraft} disabled={isSubmitting}>Delete Draft</button>
                )}
                <button type="button" className="btn btn-outline-primary" onClick={() => handleFormSubmit(true)} disabled={isSubmitting}>Save Draft</button>
                <button type="button" className="btn btn-danger" onClick={() => handleFormSubmit(false)} disabled={isSubmitting}>
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </form>
          </div>
        </div>

        {showTypeModal && (
          <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h6 className="modal-title fw-bold mb-0">Select request form type</h6>
                  <button type="button" className="btn-close" onClick={() => setShowTypeModal(false)}></button>
                </div>
                <div className="modal-body">
                  <div className="list-group">
                    {formTypesList.map(t => (
                      <button
                        key={t.code}
                        type="button"
                        className={`list-group-item list-group-item-action ${selectedType === normalizeFormType(t.code) ? 'active' : ''}`}
                        onClick={() => {
                          setField('selectRequestFromType', normalizeFormType(t.code));
                          setShowTypeModal(false);
                        }}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {showItemModal && (
          <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h6 className="modal-title fw-bold mb-0">Add Line</h6>
                  <button type="button" className="btn-close" onClick={() => setShowItemModal(false)}></button>
                </div>
                <div className="modal-body">
                  <label className="form-label small fw-semibold">Sr. No. <span className="text-danger">*</span></label>
                  <input className="form-control mb-2 bg-light" value={String(items.length + 1).padStart(2, '0')} readOnly />
                  <label className="form-label small fw-semibold">Items <span className="text-danger">*</span></label>
                  <select className="form-select mb-2" value={newItem.Items} onChange={e => {
                    const opt = itemOptionsList.find(o => o.code === e.target.value);
                    setNewItem({ ...newItem, Items: e.target.value, ItemLabel: opt?.label || '' });
                  }}>
                    <option value="">Select option</option>
                    {itemOptionsList.map(o => <option key={o.code} value={o.code}>{o.label}</option>)}
                  </select>
                  <label className="form-label small fw-semibold">Remarks</label>
                  <textarea className="form-control" rows={3} value={newItem.Remarks} onChange={e => setNewItem({ ...newItem, Remarks: e.target.value })} />
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-light btn-sm" onClick={() => setShowItemModal(false)}>Cancel</button>
                  <button type="button" className="btn btn-danger btn-sm" onClick={saveItem}>Add</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ERequisitionITCreate;
